import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, CreditCard, RefreshCw } from "lucide-react";

const TIERS = [
  { id: "starter", name: "Starter", price: 29, perks: [
      "Up to 10 listings / month",
      "Full marketing kit: description, neighborhood, social, headlines, email, video script & flyer",
      "Fair Housing compliance checker on every listing",
      "Photo-to-copy & smart feature detection",
      "Address auto-fill + voice-to-listing dictation",
      "Agent voice profile, tone re-spin & buyer targeting",
    ] },
  { id: "pro", name: "Pro", price: 69, popular: true, trial: "7-day free trial", perks: [
      "Everything in Starter, plus:",
      "Unlimited listings",
      "AI Coach mode — live engagement score + 1-tap improve",
      "Branded social graphics & \"Just Listed\" video reels",
      "1-click property landing pages with lead capture",
      "Multilingual output in 6 languages",
      "Full toolkit: offer letters, ad copy, testimonials, buyer-match & follow-up sequences",
      "Share to Facebook, LinkedIn, X & Instagram",
    ] },
  { id: "brokerage", name: "Brokerage / Elite", price: 249, perks: [
      "Everything in Pro, plus:",
      "Bulk CSV listing generation",
      "Brokerage Intelligence: Performance Predictor & A/B Test Lab",
      "Compliance presets — auto-enforce banned phrases",
      "Performance Analytics: views & leads over time",
      "White-label branding & team seats",
      "Priority support",
    ] },
];

export default function BillingView({ onChange }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [polling, setPolling] = useState(false);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setPolling(false); toast.error("Still processing — refresh in a moment."); return; }
    try {
      const { data } = await api.get(`/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPolling(false);
        const value = TIERS.find((t) => t.id === data.plan)?.price ?? 1.0;
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
          window.gtag("event", "conversion", {
            send_to: "AW-18250718276/eNyOCOy61sEcEMS4z_5D",
            value,
            currency: "USD",
            transaction_id: sessionId,
          });
        }
        toast.success(`You're now on the ${data.plan?.toUpperCase?.() || "new"} plan!`);
        onChange?.();
        return;
      }
      if (data.status === "expired") { setPolling(false); toast.error("Checkout expired."); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch {
      setPolling(false);
    }
  }, [onChange]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) {
      setPolling(true);
      poll(sessionId);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, [params, setParams, poll]);

  const upgrade = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { plan: "", origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setBusy(null);
    }
  };

  const isPaid = user?.plan && user.plan !== "free";

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-lg p-6 flex items-center justify-between flex-wrap gap-4" data-testid="current-plan-banner">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-slate-700" />
          <div>
            <p className="text-sm text-slate-500">Current plan</p>
            <p className="font-semibold text-lg capitalize">{user?.plan || "Free"}</p>
          </div>
        </div>
        {polling && <span className="flex items-center gap-2 text-sm text-slate-600"><RefreshCw className="h-4 w-4 animate-spin" /> Confirming payment…</span>}
        {isPaid && !polling && (
          <Button onClick={openPortal} disabled={busy === "portal"} variant="outline" data-testid="manage-billing-btn">
            {busy === "portal" ? "Opening…" : "Manage subscription"}
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6 items-start">
        {TIERS.map((t) => {
          const current = user?.plan === t.id;
          return (
            <div key={t.id} data-testid={`billing-tier-${t.id}`}
              className={`relative rounded-lg p-6 border bg-white ${t.popular ? "border-slate-900 ring-1 ring-slate-900" : "border-slate-200"}`}>
              {t.popular && <div className="absolute -top-3 left-6 bg-slate-900 text-white text-xs font-bold px-3 py-1 rounded-sm uppercase tracking-wide">Most Popular</div>}
              <h3 className="font-semibold text-lg">{t.name}</h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="font-display text-4xl">${t.price}</span><span className="text-slate-500 mb-1.5">/mo</span>
              </div>
              {t.trial && <p className="text-xs font-medium text-green-700 mt-1">{t.trial}</p>}
              <Button onClick={() => upgrade(t.id)} disabled={current || busy === t.id} data-testid={`billing-tier-${t.id}-btn`}
                className={`w-full mt-5 font-semibold ${t.popular ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}`} variant={t.popular ? "default" : "outline"}>
                {current ? "Current plan" : busy === t.id ? "Redirecting…" : `Upgrade to ${t.name}`}
              </Button>
              <ul className="mt-6 space-y-2.5">
                {t.perks.map((p) => (
                  p.endsWith(":")
                    ? <li key={p} className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1">{p}</li>
                    : <li key={p} className="flex gap-2.5 text-sm text-slate-600"><Check className="h-4 w-4 text-green-600 shrink-0 mt-0.5" /> {p}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-6 text-center">Payments processed securely via Stripe (test mode).</p>
    </div>
  );
}
